import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { AuthClient } from "../auth/AuthClient";
import { useAuthStore } from "../auth/authStore";
import { AuthField, AuthFields } from "../auth/components/AuthField";
import { AuthError, AuthScreen } from "../auth/components/AuthScreen";
import { AuthLinkButton, AuthSubmitButton } from "../auth/components/AuthSubmitButton";
import { APP_BASE_NAME } from "../branding";

export const Route = createFileRoute("/login")({
  component: LoginRouteView,
});

/**
 * One id for the error box on both steps: only one is ever mounted, and the
 * field of the visible step points its `aria-describedby` here.
 */
const ERROR_ID = "login-error";

/**
 * The login is one screen with two steps. A password either signs the user in
 * or yields a challenge that must be redeemed with a code from their
 * authenticator; `challenge` being non-null is what tells the two apart.
 */
function LoginRouteView() {
  const navigate = useNavigate();
  const applyUser = useAuthStore((state) => state.applyUser);
  const status = useAuthStore((state) => state.status);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "authenticated") {
      void navigate({ to: "/", replace: true });
    }
  }, [status, navigate]);

  // The WebSocket transport reconnects automatically once the cookie is set,
  // but native API singletons still hold the pre-login session, so reload.
  const finishSession = (user: Parameters<typeof applyUser>[0]) => {
    applyUser(user);
    window.location.assign("/");
  };

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await AuthClient.login(email.trim(), password);
      if (outcome.kind === "mfa-required") {
        setChallenge(outcome.challenge);
        // The password is done its job; not keeping it around means a stalled
        // second step cannot leave it sitting in component state.
        setPassword("");
        return;
      }
      finishSession(outcome.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || challenge === null) return;
    setError(null);
    setSubmitting(true);
    try {
      const trimmed = code.trim();
      const result = await AuthClient.completeTotpLogin({
        challenge,
        ...(usingRecoveryCode ? { recoveryCode: trimmed } : { code: trimmed }),
      });
      finishSession(result.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setChallenge(null);
    setCode("");
    setUsingRecoveryCode(false);
    setError(null);
  };

  // Same discriminator as above, named for the presentation below.
  const onCodeStep = challenge !== null;

  return (
    <AuthScreen
      // Remounts the form column on each step so its entrance replays; the
      // brand column is a sibling and stays where it is.
      stepKey={onCodeStep ? "code" : "password"}
      title={onCodeStep ? "Two-factor code" : "Sign in"}
      subtitle={
        onCodeStep
          ? usingRecoveryCode
            ? "Enter one of the recovery codes you saved when you set up two-factor authentication."
            : `Open your authenticator app and enter the 6-digit code for ${APP_BASE_NAME}.`
          : "Use the credentials provided by your administrator."
      }
      onSubmit={onCodeStep ? handleCodeSubmit : handlePasswordSubmit}
      footer={
        onCodeStep ? (
          <div className="auth-linkrow">
            <AuthLinkButton
              onClick={() => {
                setUsingRecoveryCode((previous) => !previous);
                setCode("");
                setError(null);
              }}
            >
              {usingRecoveryCode ? "Use authenticator code" : "Use a recovery code"}
            </AuthLinkButton>
            <AuthLinkButton onClick={restart}>Back</AuthLinkButton>
          </div>
        ) : undefined
      }
    >
      <AuthFields>
        {onCodeStep ? (
          <AuthField
            id="totp-code"
            label={usingRecoveryCode ? "Recovery code" : "Code"}
            // A recovery code is too long for the wide-tracked treatment.
            variant={usingRecoveryCode ? "mono" : "code"}
            // `autoFocus` is right here: this step exists only because the
            // user already committed to signing in.
            autoFocus
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete={usingRecoveryCode ? "off" : "one-time-code"}
            inputMode={usingRecoveryCode ? "text" : "numeric"}
            placeholder={usingRecoveryCode ? "XXXXX-XXXXX" : "000000"}
            maxLength={usingRecoveryCode ? 16 : 6}
            required
            // The only thing that can be wrong on this step is the code.
            // Truthiness, not `!== null`: the server can hand us an empty
            // message, and an unexplained red field is worse than no field.
            aria-invalid={Boolean(error)}
            aria-describedby={error ? ERROR_ID : undefined}
          />
        ) : (
          <>
            <AuthField
              id="email"
              label="Email"
              type="email"
              autoComplete="username"
              placeholder="name@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <AuthField
              id="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              // A rejected sign-in doesn't say which half was wrong, and the
              // password is the half worth retyping — so only it is marked,
              // and never the code field of the step that isn't showing.
              aria-invalid={Boolean(error)}
              aria-describedby={error ? ERROR_ID : undefined}
            />
          </>
        )}
      </AuthFields>

      {error ? <AuthError id={ERROR_ID}>{error}</AuthError> : null}

      <AuthSubmitButton pending={submitting}>
        {onCodeStep
          ? submitting
            ? "Verifying…"
            : "Verify"
          : submitting
            ? "Signing in…"
            : "Sign in"}
      </AuthSubmitButton>
    </AuthScreen>
  );
}
